import {
    ARCINTBSolutions,
    ARCTBCFSolutions,
    MLCData,
    MLCDataEntries,
    MLCINTBWarningIndexGroupedData,
    MLCTBCFWarningIndexGroupedData,
    MLCTrendsData,
    MLCType,
    MLCWarningIndexData,
    MLCWarningIndexGroupedData,
    ProcessLine,
    TrendsSingleParamData,
    WarningIndexMessage,
    WarningIndexSingleParamData
} from "../../types";

export const findINTBData = (data: MLCData): boolean => data.Line && data.Line !== '';
export const MLCOverviewFilter = (data: MLCData) => data.MlcType === MLCType.OVERVIEW;
export const MLCWarningIndexFilter = (data: MLCData) => data.MlcType === MLCType.WARNINGINDEX;
export const MLCTrendsFilter = (data: MLCData) => data.MlcType === MLCType.TRENDS;

export const INTBFilter = (data: MLCData) => findINTBData(data);
export const TBCFFilter = (data: MLCData) => !findINTBData(data);

export const lineFilter = (line: ProcessLine) => ((data: MLCData) => data.Line === line);

export const ignoreTimestamp = (data: any) => {
    data.Timestamp && delete data.Timestamp
    return data;
};

export const setFalseInNoArianeIdentifier = (data: any) => {
    if(!data.Ariane_Identifier) {
        data.Ariane_Identifier = 'false';
    }
    return data;
}

export const findParamEntries = (data: MLCTrendsData, paramName: string): MLCDataEntries | undefined => {
    const entries = Object.entries(data);
    const hasParam = entries.some(([param,]) => {
        const [solution] = param.split(' ');
        return solution === paramName;
    });
    if (!hasParam) {
        return undefined;
    };
    const one = entries.find(([param,]) => {
        const [solution] = param.split(' ');
        return solution === paramName;
    });
    return entries;
};

export const groupOneParam = (data: MLCTrendsData, paramName: string): TrendsSingleParamData | undefined => {
    const entries = findParamEntries(data, paramName);
    if (!entries) {
        return entries as undefined;
    };
    const result = entries.reduce((acc, [param, val]) => {
        if (['Line', 'MlcType', 'Timestamp'].includes(param)) {
            return Object.assign({}, acc, { [param]: val })
        };
        const [name, parameters] = param.split(' ');
        if (name.toLowerCase() === paramName.toLowerCase()) {
            return Object.assign({}, acc, { [parameters]: val });
        }
        return acc;
    }, {} as TrendsSingleParamData);
    return result;
};

export const scanParamWithWarningIndex = (acc, val: any) => {
    const index = acc.findIndex(el => el.ParamName === val.ParamName)
    if (index !== -1) {
        acc[index] = val
    } else {
        acc.push(val)
    }
    return [...acc];
};

export const reduceToMaxFourElement = (data: any) => {
    return data.reduce((acc, val, index) => {
        if (index < 4) {
            acc.push(val)
        }
        return [...acc];
    }, [])
};

export const groupParamsBasedOnIndex = (data: MLCTBCFWarningIndexGroupedData): WarningIndexMessage[] => {
    return Object.entries(data).reduce((acc, [key, val]) => {
        if (val.Warning_Index !== undefined) {
            const { Warning_Index, ParamName, Line, Acknowledge, ParameterGroup } = val;
            acc.push({ Warning_Index: parseInt(`${Warning_Index}`), ParamName, Line, ParsedParamName: key, ParameterGroup, Acknowledge: Acknowledge })
            return acc;
        }
        return acc
    }, [] as WarningIndexMessage[])
};

type GroupedParams = MLCWarningIndexGroupedData | MLCINTBWarningIndexGroupedData | MLCTBCFWarningIndexGroupedData
export const groupWIParams = (data: MLCWarningIndexData | MLCData, withLine: boolean = false): GroupedParams => {
    const { Line, Timestamp, ParameterGroup, MlcType, ...restData } = data;
    const groupedData = Object.entries(restData).reduce((acc, [param, val]) => {
        const [solution, parameters] = param.split(' ');
        if (parameters === 'Warning_Index') {
            val = parseInt(val as string)
        }
        const solutionName = solution.replace(/_/g, ' ');
        const valueObj = Object.assign({}, acc[solution], { [parameters]: val }, { ParamName: solutionName });
        return Object.assign({}, acc, { [solution]: valueObj })
    }, {});

    for (const key in groupedData) {
        if (Line) {
            groupedData[key].Line = Line;
        }
        if (!groupedData[key].Acknowledge) {
            groupedData[key].Acknowledge = null;
        }
        if (Number(groupedData[key].Target) === 0) {
            groupedData[key].CriteriaHigh = groupedData[key].CriteriaLow = 0;
        }
        groupedData[key].Timestamp = Timestamp;
        groupedData[key].ParameterGroup = ParameterGroup;
    };

    return withLine ? { [Line]: groupedData } : groupedData;
};

const setParam = (ParamName: string, getTarget: boolean, Line = null) => {
    return { ParamName, getTarget, ...(Line) && { Line } } as WarningIndexSingleParamData;
}

export const buildMLCSolutions = (processes: ARCINTBSolutions[] | ARCTBCFSolutions[], dryers: string[], processLines: string, isINTB: boolean) => {
    const MLCSolutions: MLCWarningIndexGroupedData = isINTB
        ? {
            // INTB Static Params
            'TB_BU-BR_Delta_OV_Deviation': setParam('TB BU-BR Delta OV Deviation', true, processLines),
            'TB_BU-BR_Delta_OV_Deviation_TotalBR_OV': setParam('TB BU-BR Delta OV Deviation TotalBR OV', true, processLines),
            'TB_BU-BR_Delta_OV_Deviation_TotalBU_OV': setParam('TB BU-BR Delta OV Deviation TotalBU OV', true, processLines),
        }
        : dryers.reduce((acc, dryer) => {
            if (dryer === 'FTD') {
                return Object.assign(acc, {
                    FTD_Gas_Temperature: setParam('FTD Gas Temperature', true),
                    FTD_Inlet_OV: setParam('FTD Inlet OV', true),
                });
            }
            return Object.assign(acc, {
                Dryer_Exit_Tobacco_Temperature: setParam('Dryer Exit Tobacco Temperature', true),
                Dryer_Inlet_OV: setParam('Dryer Inlet OV', true),
            });
        }, { Final_OV: setParam('Final OV', false) })

    processes.forEach(process => {
        if (isINTB && [ARCINTBSolutions.BU, ARCINTBSolutions.BR, ARCINTBSolutions.CL].includes(process as ARCINTBSolutions)) {
            MLCSolutions[`${process}_Tobacco_Temperature`] = setParam(`${process} Tobacco Temperature`, true, processLines);
        } else {
            MLCSolutions[`${process}_Application_Deviation`] = setParam(`${process} Application Deviation`, true, processLines);
            MLCSolutions[`${process}_Quantity_Deviation`] = setParam(`${process} Quantity Deviation`, false, processLines);
        }
    });
    return MLCSolutions
};